import { Configuration } from 'msal';
import { stringify } from 'query-string';
import { Dict } from '../../models';
import {
  ApiResponse,
  CancellationData,
  Customer,
  EcomCoupon,
  Lead,
  ListSort,
  PaginatedApiResponse,
} from '../models';
import {
  ConfigurationEcomPlan,
  CreateTransferPayment,
  LeadConfiguration,
  QuotationNumbersList,
  CreateTradeInSettlement,
  ConfigurationExcessAmountAdjustment,
  PreBookingCombinations,
  CreatePreBooking,
  PreBookingVehicleCombinations,
  EcomAccessoryItem,
} from '../models/configuration';
import * as http from './http';
import ApiService from './service';

export class ConfigurationService extends ApiService {
  private mapEcomPlan(
    plan?: ConfigurationEcomPlan
  ): Omit<ConfigurationEcomPlan, 'optionsList'> | undefined {
    if (!plan) {
      return undefined;
    }
    const mapped: Omit<ConfigurationEcomPlan, 'optionsList'> & {
      optionsList?: ConfigurationEcomPlan['optionsList'];
    } = {
      ...plan,
    };
    delete mapped.optionsList;
    return mapped;
  }
  public async createConfiguration(config: LeadConfiguration) {
    const data = {
      leadId: config.lead?._id,
      ecomConfigID: config.ecomConfigID,
      vehicle: {
        modelCode: config.vehicle?.model.modelCode,
        variantCode: config.vehicle?.variant.variantCode,
        year: config.vehicle?.model.year,
        interiorColorCode: config.vehicle?.interior.colorCode,
        exteriorColorCode: config.vehicle?.exterior.colorCode,
      },
      accessories: config.accessories,
      accessoryPackages: config.accessoryPackages,
      addons: config.addons,
      insurance: this.mapEcomPlan(config.insurance),
      servicePackage: this.mapEcomPlan(config.servicePackage),
      customerCare: this.mapEcomPlan(config.customerCare),
      extendedWarranty: this.mapEcomPlan(config.extendedWarranty),
      tpl: this.mapEcomPlan(config.tpl),
      registration: this.mapEcomPlan(config.registration),
      tradeIns: config.tradeIns,
      finance: config.finance,
      campaigns: config.campaigns,
      campaignCashbackItems: config.campaignCashbackItems,
      coupons: config.coupons,
      vouchers: config.vouchers,
      accessoriesCampaignBalance: config?.accessoriesCampaignBalance || 0,
    };
    const url = `${this.apiDomain}/api/configuration`;
    const response = await http.post<ApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  public async createPrebooking(data: CreatePreBooking) {
    const url = `${this.apiDomain}/api/pre-booking`;
    const response = await http.post<ApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  public async updateConfiguration(
    configId: string,
    config: LeadConfiguration
  ) {
    const data = {
      leadId: config.lead?._id,
      ecomConfigID: config.ecomConfigID,
      vehicle: {
        modelCode: config.vehicle?.model.modelCode,
        variantCode: config.vehicle?.variant.variantCode,
        year: config.vehicle?.model.year,
        interiorColorCode: config.vehicle?.interior.colorCode,
        exteriorColorCode: config.vehicle?.exterior.colorCode,
      },
      accessories: config.accessories,
      accessoryPackages: config.accessoryPackages,
      addons: config.addons,
      insurance: this.mapEcomPlan(config.insurance),
      servicePackage: this.mapEcomPlan(config.servicePackage),
      customerCare: this.mapEcomPlan(config.customerCare),
      extendedWarranty: this.mapEcomPlan(config.extendedWarranty),
      tpl: this.mapEcomPlan(config.tpl),
      registration: this.mapEcomPlan(config.registration),
      tradeIns: config.tradeIns,
      finance: config.finance,
      campaigns: config.campaigns,
      campaignCashbackItems: config.campaignCashbackItems,
      coupons: config.coupons,
      vouchers: config.vouchers,
      accessoriesCampaignBalance: config?.accessoriesCampaignBalance || 0,
    };
    const url = `${this.apiDomain}/api/configuration/${configId}`;
    const response = await http.put<ApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );
    return response.data;
  }

  async deleteConfiguration(leadId: string, configId: string) {
    const url = `${this.apiDomain}/api/configuration?${stringify({
      leadId,
      configId,
    })}`;
    const response = await http.remove<ApiResponse<LeadConfiguration>>(
      url,
      this.store
    );
    return response.data;
  }

  async getConfiguration(leadId: string, configId: string) {
    const url = `${this.apiDomain}/api/configuration?${stringify({
      leadId,
      configId,
    })}`;
    const response = await http.get<ApiResponse<LeadConfiguration>>(
      url,
      this.store
    );
    return response.data;
  }

  async getBuildVehicleData(
    leadId: string,
    configId: string,
    customerId: string
  ) {
    const url = `${
      this.apiDomain
    }/api/configuration/build-vehilcle-data?${stringify({
      leadId,
      configId,
      customerId,
    })}`;
    const response = await http.get<
      ApiResponse<{
        configuration?: LeadConfiguration;
        lead: Lead;
        customer: Customer;
      }>
    >(url, this.store);
    return response.data;
  }

  async applyDiscount(
    data: { discount: number; comments?: string },
    configurationId: string
  ) {
    const url = `${this.apiDomain}/api/configuration/${configurationId}/discount`;
    const response = await http.put<ApiResponse<Lead>>(url, data, this.store);
    return response.data;
  }

  async addCoupon(
    configurationId: string,
    coupon: EcomCoupon
  ): Promise<EcomCoupon[] | undefined> {
    const url = `${this.apiDomain}/api/configuration/${configurationId}/coupons`;
    const response = await http.post<ApiResponse<LeadConfiguration>>(
      url,
      coupon,
      this.store
    );
    return response.data?.data?.coupons;
  }

  async removeCoupon(
    configurationId: string,
    couponCode: string
  ): Promise<EcomCoupon[] | undefined> {
    const url = `${
      this.apiDomain
    }/api/configuration/${configurationId}/coupons?${stringify({
      couponCode,
    })}`;
    const response = await http.remove<ApiResponse<LeadConfiguration>>(
      url,
      this.store
    );
    return response.data?.data?.coupons;
  }

  async downloadConfiguration(
    configurationId: string,
    pdfName?: string,
    invoiceNumber?: string,
    isCanceled?: boolean,
    showDiscountDetails?: boolean
  ) {
    const url = `${this.apiDomain}/api/configuration/${configurationId}/download/pdf`;
    const reponse = await http.downloadPostData(
      url,
      { invoiceNumber, canceled: isCanceled, showDiscountDetails },
      this.store
    );
    http.saveDownloadedFile(
      reponse.data,
      'application/pdf',
      `${pdfName || 'Quotation'}.pdf`
    );
    return;
  }

  public async downloadConfigurationReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/report/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Report-${new Date()}.xlsx`
    );
  }

  public async getConfigurationReports(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Lead> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/configuration/report`;

    const resposne = await http.post<PaginatedApiResponse<Lead>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async getActivityReportDetails(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Lead> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/activity/report-detail`;

    const resposne = await http.post<PaginatedApiResponse<Lead>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async getSiteVisitReportModal(
    data: {
      page?: number;
      search?: string;
      perPage?: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Lead> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/siteVisit/report-list `;
    const resposne = await http.post<PaginatedApiResponse<Lead>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async getDiscountSummaryReport(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<
    PaginatedApiResponse<LeadConfiguration> & { lastUpdatedAt: Date }
  > {
    const url = `${this.apiDomain}/api/configuration/discount-summary-report`;

    const resposne = await http.post<PaginatedApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async downloadDiscountSummaryReport(
    data: {
      search?: string;
      listView: string;
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/discount-summary-report/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `DiscountSummary-${new Date()}.xlsx`
    );
  }

  async getQuotationNumbers(
    customerId: string
  ): Promise<ApiResponse<QuotationNumbersList[]>> {
    const url = `${this.apiDomain}/api/configuration/customer/${customerId}/quotation-numbers`;

    const resposne = await http.get<ApiResponse<QuotationNumbersList[]>>(
      url,
      this.store
    );

    return {
      ...resposne.data,
    };
  }

  async createTransferPayment(data: CreateTransferPayment, configId: string) {
    const url = `${this.apiDomain}/api/configuration/${configId}/payment/transfer`;
    const response = await http.post<ApiResponse<PaymentResponse>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }

  public async cancelOrderRequest(id: string, data: CancellationData) {
    const url = `${this.apiDomain}/api/configuration/${id}/cancel/request`;

    const response = await http.post<ApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async getCancelRequests(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<
    PaginatedApiResponse<LeadConfiguration> & { lastUpdatedAt: Date }
  > {
    const url = `${this.apiDomain}/api/configuration/list`;
    const response = await http.post<PaginatedApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );
    return { ...response?.data, lastUpdatedAt: new Date() };
  }

  public async acceptCancelRequest(
    configId: string,
    data: {
      comments: string;
    }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/${configId}/cancel/approve`;
    await http.put(url, data, this.store);
  }

  public async rejectCancelRequest(
    configId: string,
    data: {
      comments: string;
    }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/${configId}/cancel/reject`;
    await http.put(url, data, this.store);
  }

  async tradeInSettlement(
    data: CreateTradeInSettlement,
    configId: string,
    tradeInId: string
  ) {
    const url = `${this.apiDomain}/api/configuration/${configId}/trade-in/${tradeInId}/settlement`;
    const response = await http.post<ApiResponse<PaymentResponse>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }

  public async acceptCancelInvoiceRequest(
    configId: string,
    data: {
      comments: string;
    }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/${configId}/invoice/cancel/approve`;
    await http.put(url, data, this.store);
  }

  public async rejectCancelInvoiceRequest(
    configId: string,
    data: {
      comments: string;
    }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/${configId}/invoice/cancel/reject`;
    await http.put(url, data, this.store);
  }
  async addConfigurationExcessAmount(
    data: ConfigurationExcessAmountAdjustment,
    configId: string
  ) {
    const url = `${this.apiDomain}/api/configuration/${configId}/excess-amount-adjustments`;
    const response = await http.post<ApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );
    return response.data?.data;
  }
  async deleteConfigurationExcessAmount(configId: string, id: string) {
    const url = `${this.apiDomain}/api/configuration/${configId}/excess-amount-adjustments/${id}`;
    const response = await http.remove<ApiResponse<LeadConfiguration>>(
      url,
      this.store
    );
    return response.data?.data;
  }
  public async updateExpectedDeliveryDate(date: Date, id?: string) {
    const url = `${this.apiDomain}/api/configuration/${id}/expected-delivery-date`;
    await http.put(
      url,
      {
        expectedDeliveryDate: date,
      },
      this.store
    );
  }

  public async removeExpectedDeliveryDate(id?: string) {
    const url = `${this.apiDomain}/api/configuration/${id}/expected-delivery-date`;
    await http.remove(url, this.store);
  }

  public async updateInvoiceLockedDate(id?: string) {
    const url = `${this.apiDomain}/api/configuration/${id}/lock-date`;
    await http.put(url, {}, this.store);
  }

  public async removeInvoiceLockedDate(id?: string) {
    const url = `${this.apiDomain}/api/configuration/${id}/remove-lock-date`;
    await http.put(url, {}, this.store);
  }

  async getPreBookingCombinations() {
    const url = `${this.apiDomain}/api/pre-vin/combinations`;
    const response = await http.get<
      ApiResponse<PreBookingVehicleCombinations[]>
    >(url, this.store);
    return response.data;
  }

  async pushAccessories(
    configurationId: string,
    data: {
      accessoryIDs?: string[];
      accessoryPackageIDs?: string[];
    }
  ) {
    const url = `${this.apiDomain}/api/configuration/${configurationId}/accessories/push`;
    const response = await http.put(url, data, this.store);
    return response.data;
  }

  async pullAccessories(
    configurationId: string,
    data: {
      accessoryIDs?: string[];
      accessoryPackageIDs?: string[];
    }
  ) {
    const url = `${this.apiDomain}/api/configuration/${configurationId}/accessories/pull`;
    const response = await http.put(url, data, this.store);
    return response.data;
  }

  async downloadCreditNote(
    configId: string,
    tradeInId: string,
    creditNoteDate: Date
  ) {
    const url = `${this.apiDomain}/api/configuration/${configId}/trade-in/${tradeInId}/credit-note/download`;
    const reponse = await http.downloadPostData(
      url,
      { date: creditNoteDate },
      this.store
    );
    http.saveDownloadedFile(reponse.data, 'application/pdf', `credit-note.pdf`);
    return;
  }
  async getPreVinCount(sku: string) {
    const url = `${this.apiDomain}/api/pre-vin/count?${stringify({ sku })}`;
    const response = await http.get<ApiResponse<number>>(url, this.store);
    return response.data;
  }

  async getPaymentDetails(configId: string) {
    const url = `${this.apiDomain}/api/configuration/${configId}/payment-details`;
    const response = await http.get<ApiResponse<any>>(url, this.store);
    return response.data;
  }
  async getInvoiceDetailsByVSB(configId: string) {
    const url = `${this.apiDomain}/api/configuration/${configId}/invoice-details-ByVSB`;
    const response = await http.get<ApiResponse<any>>(url, this.store);
    return response;
  }
  async getAutolineInvoiceInfo(configId: string) {
    const url = `${this.apiDomain}/api/configuration/${configId}/autoline-invoice-info`;
    const response = await http.get<ApiResponse<any>>(url, this.store);
    return response?.data;
  }
  public async getConfigurationAccessoryReport(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Lead> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/configuration/accessory-report`;

    const resposne = await http.post<PaginatedApiResponse<Lead>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }
  public async downloadConfigurationAccessoryReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/accessory-report/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Report-${new Date()}.xlsx`
    );
  }
  public async getConfigurationCouponReport(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Lead> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/configuration/coupon-report`;

    const resposne = await http.post<PaginatedApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }
  public async downloadConfigurationCouponReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/coupon-report/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `CouponReport-${new Date()}.xlsx`
    );
  }
  public async getConfigurationInsuranceReport(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Configuration> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/configuration/insurance-report`;

    const resposne = await http.post<PaginatedApiResponse<Configuration>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }
  public async downloadConfigurationInsuranceReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/insurance-report/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `InsuranceReport-${new Date()}.xlsx`
    );
  }

  public async getConfigurationServicePackageReport(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Configuration> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/configuration/service-package-report`;

    const resposne = await http.post<PaginatedApiResponse<Configuration>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async getBookingManipulationReport(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<any> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/booking-manipulation`;

    const resposne = await http.post<PaginatedApiResponse<any>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async downloadConfigurationServicePackageReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/configuration/service-package-report/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `ServicePackageReport-${new Date()}.xlsx`
    );
  }

  public async downloadBookingManipulationReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/booking-manipulation/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `BookingManipulationReport-${new Date()}.xlsx`
    );
  }

  public async getAccessoriesList(
    data: {
      page?: number;
      search?: string;
      perPage?: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<EcomAccessoryItem>> {
    const url = `${this.apiDomain}/api/configuration/accessories`;
    const response = await http.post<PaginatedApiResponse<EcomAccessoryItem>>(
      url,
      data,
      this.store
    );
    return response?.data;
  }

  public async getAccessoriesTypeList() {
    const url = `${this.apiDomain}/api/configuration/accessories-type`;
    const response = await http.get<ApiResponse<{ _id: string }[]>>(
      url,
      this.store
    );
    return response?.data?.data;
  }

  public async getDeliveryFollowUp(
    data: {
      page: number;
      search?: string;
      perPage: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<
    PaginatedApiResponse<LeadConfiguration> & { lastUpdatedAt: Date }
  > {
    const url = `${this.apiDomain}/api/configuration/delivery-follow-up`;

    const resposne = await http.post<PaginatedApiResponse<LeadConfiguration>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async getDeliveryFollowUpCounts(
    data: {
      page?: number;
      search?: string;
      perPage?: number;
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<
    ApiResponse<{
      upcoming: number;
      overdue: number;
      completed: number;
    }> & { lastUpdatedAt: Date }
  > {
    const url = `${this.apiDomain}/api/configuration/delivery-follow-up-count`;

    const resposne = await http.post<
      ApiResponse<{
        upcoming: number;
        overdue: number;
        completed: number;
      }>
    >(url, data, this.store);
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async updateVehicleDeliveryStatusFirstLevel(data: {
    leadId: string;
    configId: string;
    level: string;
    reason: string[];
    comment: string;
  }): Promise<ApiResponse<any>> {
    const url = `${this.apiDomain}/api/vin/update-status/delivery/first-level`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);

    return response?.data;
  }

  public async updateVehicleDeliveryStatusSecondLevel(data: {
    leadId: string;
    configId: string;
    level: string;
    reason: string[];
    comment: string;
  }): Promise<ApiResponse<any>> {
    const url = `${this.apiDomain}/api/vin/update-status/delivery/second-level`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);

    return response?.data;
  }

  public async updateVehicleDeliveryStatusFinalLevel(data: {
    leadId: string;
    configId: string;
    level: string;
    reason: string[];
    comment: string;
  }): Promise<ApiResponse<any>> {
    const url = `${this.apiDomain}/api/vin/update-status/delivery/final-level`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);

    return response?.data;
  }
}
